import { useState } from "react";
import { ChevronDown, RefreshCw } from "lucide-react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "components/ui/collapsible";
import { cn } from "@/lib/utils";
import OfmSchedulersList from "components/ofm-schedulers-list";
import SchedulerJobs from "@/pages/tax/extracao-nf-ofm/components/scheduler-jobs";
import { useGetJobReprocessSchedulersQuery } from "@/services/tax-closing";

const SCHEDULERS_POLL_MS = 15000;

interface ReprocessSchedulersPanelProps {
  jobId: string;
}

/**
 * Acompanhamento dos reprocessamentos OFM deste job (lojas + período),
 * com polling — mesma UI da tela /fiscal/extracao-nf. Exibido apenas
 * quando há ao menos um agendamento relevante.
 */
export function ReprocessSchedulersPanel({
  jobId,
}: ReprocessSchedulersPanelProps) {
  const { data } = useGetJobReprocessSchedulersQuery(jobId, {
    pollingInterval: SCHEDULERS_POLL_MS,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [expanded, setExpanded] = useState(false);
  const items = data?.items ?? [];

  if (items.length === 0) return null;

  return (
    <Collapsible
      open={expanded}
      onOpenChange={setExpanded}
      className="border rounded-lg bg-background"
    >
      <CollapsibleTrigger className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold hover:bg-muted/40 transition-colors rounded-lg">
        <span className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4" />
          Reprocessamentos OFM
          <span className="text-xs font-normal text-muted-foreground">
            ({items.length})
          </span>
        </span>
        <ChevronDown
          className={cn(
            "h-4 w-4 text-muted-foreground transition-transform",
            expanded && "rotate-180",
          )}
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="px-2 pb-2">
        <OfmSchedulersList
          items={items}
          isLoading={false}
          open={open}
          onToggle={(id: string) => setOpen((m) => ({ ...m, [id]: !m[id] }))}
          renderExpanded={(id: string, codInvoiceModel?: string) => (
            <SchedulerJobs schedulerId={id} codInvoiceModel={codInvoiceModel} />
          )}
        />
      </CollapsibleContent>
    </Collapsible>
  );
}
